import { axialHexDistance } from '../../hexmap/coords';
import { getHexTileMapKey } from '../../hexmap/model/keys';
import { axialNeighbors, getTile, isPassable } from '../../hexmap/model/map';
import { findPath } from '../../hexmap/pathfinding';
import { invalidateDerived } from '../derivedRegistry';
import { toMilli } from '../fixed';
import { addHeading, blockersFor, type Occupancy, occupancyOf, stopKind } from '../occupancy';
import { canFoundCity } from '../placement';
import { redirect } from '../redirect';
import { mintBuildingId } from '../state';
import { mintOrder } from '../territory';
import type { Cell, FoundFailureReason, SimContext, SimState, SystemFn, Unit } from '../types';

/**
 * Turns settler `unit` into a city site on its tile (the settler is spent).
 * The caller has checked `canFoundCity`.
 */
function foundCity(state: SimState, ctx: SimContext, unit: Unit): void {
  const def = ctx.rules.buildings.city;
  if (!def) return;
  delete state.units[unit.id];
  const id = mintBuildingId(state);
  state.buildings[id] = {
    id,
    owner: unit.owner,
    type: 'city',
    pos: { q: unit.pos.q, r: unit.pos.r },
    hp: toMilli(def.hp),
    buildProgress: 0,
  };
  invalidateDerived(state);
}

/** Records that `unit`'s found order on `target` was dropped, for its owner's client (and drops it). */
function failFound(state: SimState, unit: Unit, target: Cell, reason: FoundFailureReason): void {
  delete unit.order;
  const player = state.players[unit.owner];
  if (!player) return;
  player.lastFoundFailure = {
    tick: state.tick,
    unitId: unit.id,
    target: { q: target.q, r: target.r },
    reason,
  };
}

/**
 * Where `unit` founds instead of `target`, which an own unit holds: a tile
 * next to it with no building, that it can stop on (no other unit there or
 * heading there), where `canFoundCity` passes, and that it can walk to.
 * Its own tile first (no walk), then nearer to it, then neighbour order.
 * The path there (just its tile when it's standing there), or null.
 */
function foundNear(
  state: SimState,
  ctx: SimContext,
  occ: Occupancy,
  unit: Unit,
  target: Cell
): Cell[] | null {
  const terrain = ctx.rules.units[unit.type]?.terrain ?? [];
  const who = { id: unit.id, owner: unit.owner, type: unit.type };
  const cells = axialNeighbors(target)
    .filter((c) => {
      if (!isPassable(getTile(ctx.terrain, c), terrain)) return false;
      if (stopKind(ctx.rules, occ, who, c, true) !== 'open') return false;
      return canFoundCity(state, ctx, unit.owner, unit.id, undefined, c).ok;
    })
    .map((c, i) => ({ c, i, d: axialHexDistance(c, unit.pos) }))
    .sort((a, b) => a.d - b.d || a.i - b.i);
  const blocked = cells.some(({ d }) => d > 0)
    ? blockersFor(state, ctx.rules, unit.owner)
    : undefined;
  for (const { c, d } of cells) {
    if (d === 0) return [c];
    const path = findPath(ctx.terrain, unit.pos, c, terrain, blocked);
    if (path) return path;
  }
  return null;
}

/**
 * Found-on-arrival (`foundAt` orders): once a settler with a `found` order
 * has stopped (after movement and settling), it founds a city where it
 * stands when that's the ordered tile and `canFoundCity` passes there.
 * When an own unit stands on the tile, it founds on the nearest valid free
 * tile next to it instead (`foundNear`; the order moves there), and drops
 * the order when there's none. A rival unit standing on it, or any unit
 * heading there, only delays it: the settler keeps the order, waits nearby
 * and walks onto the tile once it's free. Otherwise (it couldn't reach the
 * tile, or founding there isn't allowed any more) the order is dropped and
 * it waits where it is. A dropped order is recorded for its owner
 * (`PlayerState.lastFoundFailure`), so the client can say why. Settlers in
 * mint order, so two arriving next to each other in one tick resolve the
 * same way on every peer.
 */
const founding: SystemFn = (state, ctx) => {
  const settlers = Object.values(state.units)
    .filter((u) => u.order?.kind === 'found' && !u.path)
    .sort((a, b) => mintOrder(a.id) - mintOrder(b.id));
  let occ: Occupancy | undefined;
  for (const unit of settlers) {
    const order = unit.order;
    if (order?.kind !== 'found') continue;
    const there = unit.pos.q === order.target.q && unit.pos.r === order.target.r;
    if (there && !unit.inside) {
      const check = canFoundCity(state, ctx, unit.owner, unit.id);
      if (check.ok) {
        foundCity(state, ctx, unit);
        occ = undefined;
      } else failFound(state, unit, order.target, check.reason);
      continue;
    }
    occ ??= occupancyOf(state, ctx.rules);
    const key = getHexTileMapKey(order.target);
    if (unit.inside || occ.buildings.has(key)) {
      failFound(state, unit, order.target, unit.inside ? 'unknown' : 'occupied');
      continue;
    }
    const check = canFoundCity(state, ctx, unit.owner, unit.id, undefined, order.target);
    if (!check.ok) {
      failFound(state, unit, order.target, check.reason);
      continue;
    }
    // An own unit holds the tile: found next to it instead.
    const holder = state.units[occ.standing.get(key) ?? ''];
    if (holder && holder.owner === unit.owner) {
      const path = foundNear(state, ctx, occ, unit, order.target);
      if (!path) {
        failFound(state, unit, order.target, 'taken');
        continue;
      }
      const spot = path[path.length - 1] as Cell;
      unit.order = { kind: 'found', target: { q: spot.q, r: spot.r } };
      if (path.length === 1) {
        foundCity(state, ctx, unit);
        occ = undefined;
        continue;
      }
      redirect(state, unit, path);
      addHeading(occ, unit, spot);
      continue;
    }
    const who = { id: unit.id, owner: unit.owner, type: unit.type };
    if (stopKind(ctx.rules, occ, who, order.target, true) === null) continue;
    const terrain = ctx.rules.units[unit.type]?.terrain ?? [];
    const path = findPath(
      ctx.terrain,
      unit.pos,
      order.target,
      terrain,
      blockersFor(state, ctx.rules, unit.owner)
    );
    if (path === null) {
      failFound(state, unit, order.target, 'unreachable');
      continue;
    }
    redirect(state, unit, path);
    addHeading(occ, unit, order.target);
  }
};

export { foundCity, founding };
